import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ButtonCircular } from '@deliveryhero/pd-cookbook/components/ButtonCircular';
import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import { Grid } from '@deliveryhero/pd-cookbook/components/Grid';
import IcClose from '@deliveryhero/pd-cookbook/components/Icons/IcClose';
import IcEmail from '@deliveryhero/pd-cookbook/components/Icons/IcEmail';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';

import ActionMenu from 'components/ActionMenu';
import ButtonWithTooltip from 'components/ButtonWithTooltip';
import CallToActions from 'components/CallToActions';
import { Card } from 'components/Card';
import { EmployeeDetailFormValues } from 'components/Forms/EmployeeDetailForm/types';
import { LoadingAnimation } from 'components/LoadingAnimation';
import Page, { ActionType } from 'components/Page';
import PageHeader from 'components/PageHeader/PageHeader';
import TableList, { RowsPerPage } from 'components/TableList';
import { FormikHelpers, FormikProps } from 'formik';
import useQueryWrapperP0 from 'hooks/useQueryWrapperP0';
import useQueryWrapperP1 from 'hooks/useQueryWrapperP1';
import {
  DashboardStats,
  Id,
  SuccessResponse,
  User,
  UserBulkDeleteResponse,
  UserCreateResponse,
  UserListResponse,
  UserPayload,
  UserRoles,
  UserUploadResponse
} from 'services/api';
import { dispatchGaEvent, PageType } from 'services/google-analytics';
import { AppRoute } from 'share/types/routes';
import { MenuButton } from 'src/components/MenuButton';
import EmployeeIcon from 'src/components/svg/EmployeeIcon';
import FoodoraEmployee from 'src/components/svg/FoodoraEmployee';
import { useConfiguration } from 'src/contexts/country-config';
import useAction from 'src/hooks/useAction';
import useApi from 'src/hooks/useApi';
import useNotification from 'src/hooks/useNotification';
import useQueryParam from 'src/hooks/useQueryParam/useQueryParam';
import useUser from 'src/hooks/useUser';
import CostCenterQueryKeys from 'src/query-keys/cost-center';
import DashboardQueryKeys from 'src/query-keys/dashboard';
import DepartmentQueryKeys from 'src/query-keys/department';
import EmployeeQueryKeys from 'src/query-keys/employee';
import ExpenseCodeQueryKeys from 'src/query-keys/expense-code';
import generateQuery from 'src/utils/generate-query';
import isFoodora from 'src/utils/is-foodora/is-foodora';
import { ButtonState } from 'utils/constants';
import getInterval from 'utils/get-interval';
import isPhoneRequired from 'utils/is-phone-required/is-phone-required';

import { AddEmployee } from './AddEmployee';
import { BulkDeleteEmployee } from './BulkDeleteEmployee';
import { LabelText } from './constants';
import { EditEmployee } from './EditEmployee';
import { EmployeeListingActionMenu } from './EmployeeListingActionMenu';
import { EmployeeListingColumns } from './EmployeeListingColumns';
import { EmployeeListingFilter } from './EmployeeListingFilter';
import { UploadEmployee } from './UploadEmployee';

import './styles.scss';

const EmployeeActions = {
  Add: 'add',
  Edit: 'edit',
  Upload: 'upload',
  Delete: 'delete',
  Unlink: 'unlink',
  BulkDelete: 'bulk delete',
  ResendAction: 'resend-confirm'
};

type EmployeeActionTypes =
  | typeof EmployeeActions.Add
  | typeof EmployeeActions.Edit
  | typeof EmployeeActions.Delete
  | typeof EmployeeActions.Upload
  | typeof EmployeeActions.BulkDelete
  | typeof EmployeeActions.ResendAction;

export function EmployeeContainer() {
  const api = useApi();
  const user = useUser();
  const config = useConfiguration();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { pushNotification, dismissNotification } = useNotification();
  const [, setAction] = useAction<EmployeeActionTypes>();
  const handleClick = (action: EmployeeActionTypes) => {
    setAction(action);
  };

  useEffect(() => {
    dispatchGaEvent({ pageType: PageType.EMPLOYEE });
  }, []);

  const employeeId = parseInt(searchParams.get('id') || '');
  const currentAction = searchParams.get('action') || '';

  const [employeeToDelete, setEmployeeToDelete] = useState<User>();
  const [employeeRole, setEmployeeRole] = useState<UserRoles>(UserRoles.User);
  const [addAnother, setAddAnother] = useState<boolean>(false);

  let isDisabled = false;

  const placeholderData: DashboardStats = {
    pending_employees: 0,
    active_employees: 0,
    active_subscription: 0,
    gift_cards_count: 0,
    allowances_count: 0
  };

  // Params
  const queryParams = useQueryParam();
  const filters = [
    'search',
    'count',
    'offset',
    'departments',
    'status',
    'roles'
  ];

  const filterParams = Object.keys(queryParams)
    .filter((key) => filters.includes(key))
    .reduce((params: Record<string, string>, key: string) => {
      params[key] = queryParams[key];
      return params;
    }, {});

  const pageNumber = queryParams.offset
    ? parseInt(queryParams.offset)
    : undefined;

  const rowsPerPage = queryParams.count
    ? parseInt(queryParams.count)
    : undefined;

  const tableProps = {
    page: pageNumber
      ? Math.floor(
          pageNumber / (rowsPerPage ? rowsPerPage : RowsPerPage.TWENTYFIVE)
        )
      : undefined, // default is 0
    rowsPerPage: rowsPerPage // default is RowsPerPage.TWENTYFIVE (=25)
  };

  // queries
  const getDepartments = useQueryWrapperP1(DepartmentQueryKeys.list(), () =>
    api.company.department.list()
  );

  const getExpenseCodes = useQueryWrapperP1(ExpenseCodeQueryKeys.list(), () =>
    api.company.expenseCode.list()
  );

  const getCostCenters = useQueryWrapperP1(CostCenterQueryKeys.list(), () =>
    api.company.costCenter.list()
  );

  const getEmployee = useQueryWrapperP1(
    EmployeeQueryKeys.detail(employeeId),
    () => api.company.user.get(employeeId),
    {
      enabled: !!employeeId
    }
  );

  const departments = getDepartments.data?.data ?? [];
  const expenseCodes = getExpenseCodes.data?.data ?? [];
  const costCenters = getCostCenters.data?.data ?? [];
  const employee = getEmployee.data?.data;

  useEffect(() => {
    setEmployeeRole(employee?.role ?? UserRoles.User);
  }, [employee?.role]);

  // mutations
  const createEmployee = useMutation<UserCreateResponse, Error, UserPayload>(
    (payload) => api.company.user.create(payload)
  );

  const editEmployee = useMutation<
    SuccessResponse,
    Error,
    Array<Id | UserPayload>
  >(([user_id, user]) =>
    api.company.user.update(user_id as Id, user as UserPayload)
  );

  const deleteEmployee = useMutation<SuccessResponse, Error, Id>(
    async (user_id) => {
      return api.company.user.delete(user_id);
    }
  );

  const unlinkEmployee = useMutation<SuccessResponse, Error, Id>(
    async (user_id) => {
      return api.company.user.unlink(user_id);
    }
  );
  const resendInvitationEmail = useMutation<SuccessResponse, Error, Id>(
    async (user_id) => {
      return api.company.user.resendInvitation(user_id);
    }
  );
  const uploadEmployees = useMutation<UserUploadResponse, Error, FormData>(
    async (form) => {
      return api.company.user.upload(form);
    }
  );

  const bulkDeleteEmployees = useMutation<
    UserBulkDeleteResponse,
    Error,
    FormData
  >(async (form) => {
    return api.company.user.bulkDelete(form);
  });

  const detailFormRef = useRef<FormikProps<EmployeeDetailFormValues>>(null);

  const handleCreateEmployee = (
    formValues: EmployeeDetailFormValues,
    actions: FormikHelpers<EmployeeDetailFormValues>
  ) => {
    const values = {
      ...formValues,
      role: employeeRole,
      department_id: formValues.department_id as number, // department id will always be number from yup validation
      cost_center_id: formValues.cost_center_id || null
    };

    const employeeName = `${values.first_name} ${values.last_name}`;
    const createInProgress = pushNotification(`Adding ${employeeName}...`);

    createEmployee.mutate(values, {
      onSuccess: () => {
        dismissNotification(createInProgress);
        queryClient.invalidateQueries(EmployeeQueryKeys.list()).then(() => {
          pushNotification(`${employeeName} added!`, {
            variant: 'success'
          });
          actions.resetForm();
          setAction(EmployeeActions.Add, addAnother);
        });
      },
      onError: (e) => {
        dismissNotification(createInProgress);
        pushNotification(e.message, {
          variant: 'error',
          onClick: () => detailFormRef.current?.submitForm()
        });
      },
      onSettled: () => {
        dismissNotification(createInProgress);
        actions.setSubmitting(false);
      }
    });
  };

  const handleEditEmployee = (
    formValues: EmployeeDetailFormValues,
    _actions: FormikHelpers<EmployeeDetailFormValues>
  ) => {
    const values = {
      ...formValues,
      role: employeeRole,
      department_id: formValues.department_id as number, // department id will always be number from yup validation
      cost_center_id: formValues.cost_center_id || null // cost center id needs to be explicitely nullable in order to reset the cost center
    };
    const employeeName = `${values.first_name} ${values.last_name}`;
    const employeeId = (employee as User).id; // Employee is not undefined when edit is called

    const editInProgress = pushNotification(
      `Updating ${employeeName}'s profile...`
    );

    editEmployee.mutate([employeeId, values], {
      onSuccess: () => {
        queryClient.invalidateQueries(EmployeeQueryKeys.list());
        queryClient
          .refetchQueries(EmployeeQueryKeys.detail(employeeId))
          .then(() => {
            dismissNotification(editInProgress);
            pushNotification(`${employeeName}'s profile is updated`, {
              variant: 'success'
            });
            setAction(false);
          });
      },
      onError: () => {
        dismissNotification(editInProgress);
        pushNotification(`Unable to update ${employeeName}`, {
          variant: 'error',
          onClick: () => detailFormRef.current?.submitForm()
        });
      }
    });
  };

  const handleResendInvitationEmail = (employee: User) => {
    const resendInviteInProgressId = pushNotification(
      `Sending invite to ${employee.name}...`
    );

    resendInvitationEmail.mutate(employee.id, {
      onSuccess: () => {
        dismissNotification(resendInviteInProgressId);

        queryClient
          .refetchQueries(EmployeeQueryKeys.detail(employee.id), {
            active: true
          })
          .then(() =>
            pushNotification(`Invite resent to ${employee.name}`, {
              variant: 'success'
            })
          );
      },
      onError: () => {
        dismissNotification(resendInviteInProgressId);
        pushNotification(
          `We are unable to send the invite to ${employee.name}`,
          {
            variant: 'error',
            onClick: () => handleResendInvitationEmail(employee)
          }
        );
      }
    });
  };

  const handleAccountUnlink = (employee: User) => {
    const accountUnlinkInProgress = pushNotification(
      `Unlinking ${employee.name}'s personal account...`
    );

    unlinkEmployee.mutate(employee.id, {
      onSuccess: () => {
        dismissNotification(accountUnlinkInProgress);
        queryClient.invalidateQueries(EmployeeQueryKeys.list());
        queryClient
          .refetchQueries(EmployeeQueryKeys.detail(employee.id), {
            active: true
          })
          .then(() =>
            pushNotification(`Unlinked ${employee.name}'s personal account`, {
              variant: 'success'
            })
          );
      },
      onError: () => {
        dismissNotification(accountUnlinkInProgress);
        pushNotification(
          `We are unable to unlink ${employee.name}'s personal account`,
          {
            variant: 'error',
            onClick: () => handleAccountUnlink(employee)
          }
        );
      }
    });
  };

  const handleDelete = () => {
    const employee = employeeToDelete;

    if (!employee) {
      return;
    }

    const deleteInProgressId = pushNotification(
      `Deleting ${employee.name}'s profile...`
    );
    deleteEmployee.mutate(employee.id, {
      onSuccess: () => {
        dismissNotification(deleteInProgressId);
        queryClient.invalidateQueries(EmployeeQueryKeys.list());
        queryClient
          .refetchQueries(EmployeeQueryKeys.detail(employee.id))
          .then(() =>
            pushNotification(`${employee.name}'s profile deleted`, {
              variant: 'success'
            })
          );
      },
      onError: () => {
        dismissNotification(deleteInProgressId);
        pushNotification(`We are unable to delete ${employee.name}'s profile`, {
          variant: 'error',
          onClick: () => handleDelete()
        });
      }
    });
  };

  const handleBulkDelete = (file: File) => {
    const formData = new FormData();
    formData.append('users_to_delete_by_email', file as File); // method only called when File is not null
    const deleteInProgressId = pushNotification('Bulk deleting employees...');

    bulkDeleteEmployees.mutate(formData, {
      onSuccess: (data) => {
        queryClient.invalidateQueries(EmployeeQueryKeys.list()).then(() => {
          if (data.data.num_users_deleted != 0) {
            pushNotification(
              `${data.data.num_users_deleted} employees deleted`,
              {
                variant: 'success'
              }
            );
          } else {
            pushNotification(`${data.data.num_users_failed} employees failed`, {
              variant: 'error'
            });
          }
        });
      },
      onError: (error) => {
        pushNotification(error.message, { variant: 'error' });
      },
      onSettled: () => {
        dismissNotification(deleteInProgressId);
      }
    });
  };

  const handleExport = () => {
    api.company.user
      .exportAll()
      .then((data: unknown) => {
        const url = window.URL.createObjectURL(new Blob([data as Blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `employee_list_${new Date()
            .toLocaleDateString()
            .replaceAll('/', '')}.csv`
        );
        document.body.appendChild(link);
        link.click();
        pushNotification('Employees exported', {
          variant: 'success'
        });
      })
      .catch((e) => {
        const error = e as Error;
        const errorSnackbarId = pushNotification(error.message, {
          variant: 'error',
          onClick: () => dismissNotification(errorSnackbarId)
        });
      });
  };

  const FILE_KEY = 'users_to_upload';
  const handleUpload = (file: File) => {
    const formData = new FormData();
    formData.append(FILE_KEY, file as File); // method only called when File is not null
    const uploadInProgressId = pushNotification('Adding employees...');

    uploadEmployees.mutate(formData, {
      onSuccess: (data) => {
        queryClient.invalidateQueries(EmployeeQueryKeys.list()).then(() => {
          pushNotification(`${data.data.num_users_uploaded} employee added`, {
            variant: 'success'
          });
        });
      },
      onError: (error) => {
        pushNotification(error.message, { variant: 'error' });
      },
      onSettled: () => {
        dismissNotification(uploadInProgressId);
      }
    });
  };

  // Params for table
  const handlePageChange = (
    changes: Record<string, string | number | undefined>
  ) => {
    let params = {
      ...queryParams,
      ...changes
    };

    params = Object.keys(params).reduce((acc, key) => {
      params[key] !== undefined && (acc[key] = params[key]);
      return acc;
    }, {} as Record<string, any>);

    navigate(
      {
        pathname: AppRoute.Employee,
        search: generateQuery(params)
      },
      { replace: true }
    );
  };

  const handlePaginationChange = (
    page: number | undefined,
    rowsPerPage: number | undefined
  ) => {
    const offset = page ? (rowsPerPage ? page * rowsPerPage : page) : undefined;
    const changes = {
      ...(offset ? { offset: offset } : { offset: undefined }),
      ...(rowsPerPage
        ? { count: rowsPerPage?.toString() }
        : { count: undefined })
    };
    handlePageChange(changes);
  };

  const bulkResendInvitation = useMutation<SuccessResponse, Error, null>(
    async () => {
      return api.company.user.bulkResendInvitation();
    }
  );

  const employeeListQuery = useQueryWrapperP0(
    EmployeeQueryKeys.list(filterParams),
    () => api.company.user.list(filterParams),
    {
      select: (result: UserListResponse) => {
        return {
          ...result,
          data: result.data.map((employee) => {
            return {
              ...employee,
              departmentName: employee.department.name || ''
            };
          })
        };
      }
    }
  );

  const dashboardStatsQuery = useQueryWrapperP1(
    DashboardQueryKeys.company(),
    () => api.company.dashboard.getStats(),
    {
      onError: () => {
        pushNotification('Failed to fetch dashboard stats', {
          variant: 'error',
          autoHideDuration: 5000
        });
      },
      enabled: user.company.is_company_management_enabled
    }
  );

  const handleBulkResendInvitationEmail = () => {
    bulkResendInvitation.mutate(null, {
      onSuccess: () => {
        pushNotification('Invites resent', {
          variant: 'success',
          autoHideDuration: 5000
        });
      },
      onError: () => {
        pushNotification('We are unable to send the invites', {
          variant: 'error',
          autoHideDuration: 5000
        });
      }
    });
  };

  if (currentAction.split(',').includes(EmployeeActions.Edit)) {
    if (!employeeId) {
      return <>Invalid</>; //todo: check with design on soft 404s
    } else if (getEmployee.isLoading) {
      return <LoadingAnimation />;
    } else if (!getEmployee.isSuccess) {
      return <>Error</>; //todo: check on error fallback
    }
  }

  const dashboardStats = dashboardStatsQuery.data?.data || placeholderData;

  if (dashboardStats.resend_activation_emails_triggered_at) {
    const interval = getInterval(
      dashboardStats.resend_activation_emails_triggered_at
    );

    isDisabled =
      interval.days !== undefined && interval.days === 0 && interval.years == 0;
  }

  return (
    <Page
      actions={{
        [EmployeeActions.Add]: {
          component: (
            <AddEmployee
              onSubmit={handleCreateEmployee}
              config={config}
              departments={departments}
              costCenters={costCenters}
              expenseCodes={expenseCodes}
              employeeRole={employeeRole}
              setEmployeeRole={setEmployeeRole}
              isPhoneRequired={isPhoneRequired(user, employeeRole)}
              editable={true}
              detailFormRef={detailFormRef}
              buttons={() => (
                <>
                  <Button
                    size="small"
                    type="submit"
                    variant="secondary"
                    style={{ width: '160px' }}
                    status={
                      createEmployee.isLoading && addAnother
                        ? ButtonState.Loading
                        : ButtonState.Idle
                    }
                    disabled={createEmployee.isLoading}
                    onClick={() => {
                      setAddAnother(true);
                      detailFormRef.current?.handleSubmit();
                    }}
                  >
                    {LabelText.ADD_EMPLOYEE.SUBMIT_ANOTHER_BUTTON}
                  </Button>
                  <Button
                    size="small"
                    type="submit"
                    style={{ width: '160px' }}
                    variant="primary"
                    status={
                      createEmployee.isLoading && !addAnother
                        ? ButtonState.Loading
                        : ButtonState.Idle
                    }
                    disabled={createEmployee.isLoading}
                    onClick={() => {
                      setAddAnother(false);
                      detailFormRef.current?.handleSubmit();
                    }}
                  >
                    {LabelText.ADD_EMPLOYEE.SUBMIT_BUTTON}
                  </Button>
                </>
              )}
            />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: LabelText.ADD_EMPLOYEE.TITLE
          }
        },
        [EmployeeActions.Edit]: {
          component: (
            <EditEmployee
              config={config}
              onSubmit={handleEditEmployee}
              employee={employee as User}
              departments={departments}
              costCenters={costCenters}
              expenseCodes={expenseCodes}
              employeeRole={employeeRole}
              setEmployeeRole={setEmployeeRole}
              isPhoneRequired={isPhoneRequired(user, employeeRole)}
              editable={true}
              detailFormRef={detailFormRef}
              handleDelete={() => {
                setEmployeeToDelete(employee);
                setAction(EmployeeActions.Delete);
              }}
              handleUnlink={() => setAction(EmployeeActions.Unlink)}
              handleResendInvitationEmail={() =>
                handleResendInvitationEmail(employee as User)
              }
              handleSubscriptionRedirection={() =>
                navigate(
                  {
                    pathname: AppRoute.Subscription,
                    search: `?search=${employee?.name}`
                  },
                  { replace: true }
                )
              }
            />
          ),
          type: ActionType.FullDialog,
          options: {
            title: LabelText.EDIT_EMPLOYEE.TITLE,
            action: {
              label: LabelText.EDIT_EMPLOYEE.EDIT_BUTTON,
              callback: () => {
                detailFormRef.current?.handleSubmit();
              }
            }
          }
        },
        [EmployeeActions.Delete]: {
          component: (
            <>
              <Button
                variant="secondary"
                onClick={() => console.log('Back to profile')}
              >
                Back to profile
              </Button>
              <Button onClick={() => handleDelete()}>Delete</Button>
            </>
          ),
          type: ActionType.AppDialog,
          options: {
            title: `Delete ${employeeToDelete?.name}'s profile`,
            content:
              'Once deleted, the profile cannot be recovered and the employee can no longer enjoy corporate account benefits.'
          }
        },
        [EmployeeActions.Unlink]: {
          component: (
            <>
              <Button
                variant="secondary"
                onClick={() => console.log('Back to profile')}
              >
                Back to profile
              </Button>
              <Button onClick={() => handleAccountUnlink(employee as User)}>
                Unlink
              </Button>
            </>
          ),
          type: ActionType.AppDialog,
          options: {
            title: `Unlink ${employee?.name}'s personal account`,
            content: 'You can always link it to a personal account again.'
          }
        },
        [EmployeeActions.Upload]: {
          component: (
            <UploadEmployee
              config={config}
              handleUpload={(file) => handleUpload(file)}
              isLoading={uploadEmployees.isLoading}
            />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: LabelText.UPLOAD_EMPLOYEE.TITLE
          }
        },
        [EmployeeActions.BulkDelete]: {
          component: (
            <BulkDeleteEmployee
              config={config}
              handleUpload={(file) => handleBulkDelete(file)}
              warning={LabelText.BULK_DELETE_EMPLOYEE.WARNING}
              errorMessage={LabelText.BULK_DELETE_EMPLOYEE.ERROR_MESSAGE}
              successMessageMultiple={
                LabelText.BULK_DELETE_EMPLOYEE.SUCCESS_MESSAGE_MULTIPLE
              }
              successMessageSingle={
                LabelText.BULK_DELETE_EMPLOYEE.SUCCESS_MESSAGE_SINGLE
              }
            />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: LabelText.BULK_DELETE_EMPLOYEE.TITLE
          }
        },
        [EmployeeActions.ResendAction]: {
          component: (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setAction(EmployeeActions.ResendAction, false)}
              >
                {LabelText.CONFIRM_MODAL.CANCEL_BUTTON}
              </Button>
              <Button type="button" onClick={handleBulkResendInvitationEmail}>
                {LabelText.CONFIRM_MODAL.CONFIRM_BUTTON}
              </Button>
            </>
          ),
          type: ActionType.AppDialog,
          options: {
            title: (
              <div className="dialog">
                <div className="dialog__content">
                  <div className="dialog__content__title">
                    <IcEmail style={{ fill: '#FF2B85' }} />
                    <Typography
                      type="title-md"
                      color="neutral-primary"
                      marginLeft="xs"
                    >
                      {LabelText.CONFIRM_MODAL.TITLE}
                    </Typography>
                  </div>
                  <Typography type="paragraph-sm" color="neutral-primary">
                    {LabelText.CONFIRM_MODAL.SUBTITLE}
                  </Typography>
                </div>
                <ButtonCircular
                  ariaLabel="close-dialog-button"
                  onClick={() => setAction(EmployeeActions.ResendAction, false)}
                >
                  <IcClose fill="#E21B70" />
                </ButtonCircular>
              </div>
            ),
            content: (
              <>
                <Typography type="paragraph-md" color="neutral-primary">
                  This will be sent to all {dashboardStats.pending_employees}{' '}
                  employees who haven’t activated their accounts yet
                </Typography>
              </>
            )
          }
        }
      }}
    >
      <PageHeader
        title={LabelText.TITLE}
        subtitle={
          <Typography type="paragraph-sm">{LabelText.SUBTITLE}</Typography>
        }
        buttons={[
          <MenuButton
            key="1"
            variant="primary"
            label={LabelText.ADD_DROPDOWN_BUTTON}
            buttons={[
              <div
                key={1}
                onClick={() => handleClick(EmployeeActions.Upload)}
                data-testid="bulkUpload"
                role="button"
              >
                {LabelText.UPLOAD_BUTTON}
              </div>,
              <div
                key={2}
                onClick={() => handleClick(EmployeeActions.Add)}
                role="button"
              >
                {LabelText.ADD_BUTTON}
              </div>
            ]}
          />,
          <MenuButton
            key="2"
            variant="secondary"
            label={LabelText.MORE_BUTTON}
            data-testid="moreDropdown"
            buttons={[
              <div
                onClick={() => handleClick(EmployeeActions.BulkDelete)}
                role="button"
                data-testid="bulkDelete"
                key={1}
              >
                {LabelText.DELETE_BUTTON}
              </div>,
              <div
                onClick={handleExport}
                role="button"
                data-testid="export"
                key={2}
              >
                {LabelText.EXPORT_BUTTON}
              </div>
            ]}
          />
        ]}
      />
      <div className="employees-container">
        <Card>
          {dashboardStatsQuery.isLoading ? (
            <LoadingAnimation />
          ) : (
            <Grid
              gridTemplateColumns="auto auto auto"
              columnGap="16px"
              className="employees-container__stats__content"
            >
              <Card className="employees-container__stats__icon">
                <IcEmail />
              </Card>
              <div>
                <Typography
                  type="label-sm"
                  color="neutral-secondary"
                  marginBottom="xxs"
                >
                  {LabelText.STATS.PENDING_EMPLOYEES}
                </Typography>
                <Typography type="title-md" color="neutral-primary">
                  {dashboardStats.pending_employees}
                </Typography>
              </div>
              <ButtonWithTooltip
                displayOnlyWhenDisabled
                isDisabled={isDisabled}
                onClick={() => setAction(EmployeeActions.ResendAction)}
                lastSentOn={
                  dashboardStats.resend_activation_emails_triggered_at
                }
              >
                {LabelText.STATS.PENDING_EMPLOYEES_BUTTON}
              </ButtonWithTooltip>
            </Grid>
          )}
        </Card>
      </div>
      <TableList<User & { departmentName: string }>
        total={
          employeeListQuery.error ? 0 : employeeListQuery.data?.total ?? -1
        }
        loading={employeeListQuery.isLoading}
        empty={
          Object.keys(filterParams).length === 0 && (
            <CallToActions
              illustration={isFoodora() ? FoodoraEmployee : EmployeeIcon}
              title={LabelText.EMPLOYEE_LISTING.CTA_TITLE}
              subtitle={LabelText.EMPLOYEE_LISTING.CTA_SUBTITLE}
            >
              <Button
                data-testid="emptyBulkUpload"
                onClick={() => {
                  const searchParams = new URLSearchParams();
                  setSearchParams(searchParams);
                }}
              >
                {LabelText.EMPLOYEE_LISTING.CTA_BUTTON}
              </Button>
            </CallToActions>
          )
        }
        columns={EmployeeListingColumns}
        data={employeeListQuery.data?.data || []}
        onPaginationChange={handlePaginationChange}
        action={(employee) => (
          <ActionMenu>
            <EmployeeListingActionMenu
              handleEdit={() => {
                setAction(EmployeeActions.Edit, true, {
                  id: employee.id.toString()
                });
              }}
              handleDelete={() => {
                setEmployeeToDelete(employee);
                setAction(EmployeeActions.Delete);
              }}
            />
          </ActionMenu>
        )}
        {...tableProps}
      >
        <EmployeeListingFilter
          onPageChange={handlePageChange}
          params={filterParams}
          departments={departments}
        />
      </TableList>
    </Page>
  );
}
